import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, Stack, Chip, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from '../api/axiosConfig';

// YYYYMMDD 또는 ISO 문자열을 YYYY-MM-DD 포맷으로 변환하는 도우미 함수
const formatDateForInput = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  if (/^\d{8}$/.test(dateStr)) {
    return `${dateStr.slice(0, 4)}-${dateStr.slice(4, 6)}-${dateStr.slice(6, 8)}`;
  }
  if (dateStr.includes('T')) {
    return dateStr.split('T')[0];
  }
  return dateStr;
};

const MyProfile = ({ user }) => {
  const [profileData, setProfileData] = useState({
    id: user?.id || null,
    loginId: user?.loginId || '',
    name: user?.name || '',
    email: user?.email || '',
    birthDate: formatDateForInput(user?.birthDate || ''),
    phone: user?.phone || '',
    address: user?.address || '',
    certNumber: user?.certNumber || '',
    infoConfirmedYn: user?.infoConfirmedYn || user?.infoCheckYn || 'N',
  });

  // user props 변경 시 local state 동기화
  useEffect(() => {
    if (user) {
      setProfileData({
        id: user.id || null,
        loginId: user.loginId || '',
        name: user.name || '',
        email: user.email || '',
        birthDate: formatDateForInput(user.birthDate || ''),
        phone: user.phone || '',
        address: user.address || '',
        certNumber: user.certNumber || '',
        infoConfirmedYn: user.infoConfirmedYn || user.infoCheckYn || 'N',
      });
    }
  }, [user]);

  // 비밀번호 변경 모달 상태
  const [openPwModal, setOpenPwModal] = useState(false);
  const [pwForm, setPwForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // 내 정보 저장 (수정 시 확인 처리 Y 도 함께 업데이트)
  const handleSave = async () => {
    if (!profileData.name || !profileData.email) {
      alert('이름과 이메일은 필수 입력입니다.');
      return;
    }

    try {
      const updatePayload = {
        ...profileData,
        infoConfirmedYn: 'Y', // 수정 시 저장하면 자동으로 정보 확인 처리
        updateUser: profileData.loginId || 'user',
      };

      await axios.put(`/api/member/${profileData.id}`, updatePayload);
      
      setProfileData((prev) => ({ ...prev, infoConfirmedYn: 'Y' }));
      alert('내 정보가 성공적으로 수정되었으며, 정보 확인 처리가 완료되었습니다.');
    } catch (error) {
      console.error('내 정보 수정 오류:', error);
      alert(error.response?.data || '정보 수정 중 오류가 발생했습니다.');
    }
  };

  // 별도 변경 없이 "정보 확인 완료"만 처리하는 함수
  const handleConfirmInfo = async () => {
    try {
      const updatePayload = {
        ...profileData,
        infoConfirmedYn: 'Y',
        updateUser: profileData.loginId || 'user',
      };

      await axios.put(`/api/member/${profileData.id}`, updatePayload);

      setProfileData((prev) => ({ ...prev, infoConfirmedYn: 'Y' }));
      alert('내 정보 이상 없음 확인 처리가 완료되었습니다.');
    } catch (error) {
      console.error('정보 확인 처리 오류:', error);
      alert('확인 처리 중 오류가 발생했습니다.');
    }
  };

  // 비밀번호 변경 처리
  const handleChangePassword = async () => {
    if (!pwForm.currentPassword || !pwForm.newPassword) {
      alert('모든 비밀번호 항목을 입력해주세요.');
      return;
    }
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      alert('새 비밀번호와 비밀번호 확인이 일치하지 않습니다.');
      return;
    }

    try {
      await axios.post('/api/member/change-password', {
        memberId: profileData.id,
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword,
      });

      alert('비밀번호가 성공적으로 변경되었습니다.');
      setOpenPwModal(false);
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('비밀번호 변경 오류:', error);
      alert(error.response?.data || '비밀번호 변경 중 오류가 발생했습니다.');
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: { xs: 1, sm: 2 } }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, color: '#0f172a' }}>
        내 정보 확인 및 수정
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        회원님의 개인정보를 확인하고 수정할 수 있습니다.
      </Typography>

      <Card elevation={0} sx={{ p: { xs: 1, sm: 2 }, borderRadius: '16px', border: '1px solid #e2e8f0' }}>
        <CardContent>
          <Stack spacing={2.5}>
            {/* 권한 및 정보 확인 상태 표시 */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>계정 권한</Typography>
              <Stack direction="row" spacing={1}>
                <Chip
                  label={profileData.infoConfirmedYn === 'Y' ? '정보확인 완료' : '정보 미확인'}
                  color={profileData.infoConfirmedYn === 'Y' ? 'success' : 'warning'}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={user?.role === 'ROLE_ADMIN' ? '관리자' : user?.role === 'ROLE_TEACHER' ? '교원회원' : '일반회원'}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
              </Stack>
            </Box>

            {/* 정보 미확인 상태 시 안내 알림 */}
            {profileData.infoConfirmedYn !== 'Y' && (
              <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                회원님의 등록 정보가 맞는지 확인해 주세요. 이상이 없으면 <strong>[내 정보 이상 없음 (확인)]</strong> 버튼을 클릭해 주세요.
              </Alert>
            )}

            <Divider />

            {/* 아이디 (읽기 전용) */}
            <TextField
              fullWidth
              label="아이디"
              value={profileData.loginId}
              disabled
              InputProps={{ readOnly: true }}
              helperText="아이디는 변경할 수 없습니다."
            />

            {/* 이름 */}
            <TextField
              fullWidth
              label="이름 *"
              value={profileData.name}
              onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
            />

            {/* 이메일 */}
            <TextField
              fullWidth
              label="이메일 *"
              value={profileData.email}
              onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
            />

            {/* 생년월일 */}
            <TextField
              fullWidth
              type="date"
              label="생년월일"
              value={profileData.birthDate}
              onChange={(e) => setProfileData({ ...profileData, birthDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            {/* 연락처 */}
            <TextField
              fullWidth
              label="연락처"
              value={profileData.phone}
              onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
            />

            {/* 주소 */}
            <TextField
              fullWidth
              label="주소"
              value={profileData.address}
              onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
            />

            {/* 교원자격증 번호 */}
            <TextField
              fullWidth
              label="교원자격증 번호"
              value={profileData.certNumber}
              onChange={(e) => setProfileData({ ...profileData, certNumber: e.target.value })}
            />

            {/* 비밀번호 변경 버튼 */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<LockResetIcon />}
                onClick={() => setOpenPwModal(true)}
                sx={{ borderRadius: '10px', textTransform: 'none' }}
              >
                비밀번호 변경
              </Button>
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* 하단 저장 / 확인 버튼 세트 */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              {profileData.infoConfirmedYn !== 'Y' && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="success"
                  size="large"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleConfirmInfo}
                  sx={{ borderRadius: '10px', py: 1.2, fontWeight: 700 }}
                >
                  내 정보 이상 없음 (확인)
                </Button>
              )}
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleSave}
                sx={{ borderRadius: '10px', py: 1.2, fontWeight: 700 }}
              >
                정보 저장하기
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {/* 비밀번호 변경 모달 Dialog */}
      <Dialog open={openPwModal} onClose={() => setOpenPwModal(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>비밀번호 변경</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              type="password"
              label="현재 비밀번호 *"
              value={pwForm.currentPassword}
              onChange={(e) => setPwForm({ ...pwForm, currentPassword: e.target.value })}
            />
            <TextField
              fullWidth
              type="password"
              label="새 비밀번호 *"
              value={pwForm.newPassword}
              onChange={(e) => setPwForm({ ...pwForm, newPassword: e.target.value })}
            />
            <TextField
              fullWidth
              type="password"
              label="새 비밀번호 확인 *"
              value={pwForm.confirmPassword}
              onChange={(e) => setPwForm({ ...pwForm, confirmPassword: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenPwModal(false)}>취소</Button>
          <Button onClick={handleChangePassword} variant="contained" color="primary">
            변경하기
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyProfile;